import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { haptic } from "@/lib/haptics";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-body tracking-wide select-none touch-manipulation active:scale-[0.97] cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-gold-light hover:shadow-gold active:bg-gold-dark",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
        outline: "border-2 border-gold/40 bg-transparent text-gold hover:bg-gold/10 hover:border-gold hover:shadow-[0_0_20px_hsl(var(--gold)/0.2)] active:bg-gold/20",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
        ghost: "hover:bg-accent/10 hover:text-accent active:bg-accent/20",
        link: "text-primary underline-offset-4 hover:underline",
        gold: "bg-gradient-to-r from-gold to-gold-light text-charcoal font-semibold shadow-md hover:shadow-[0_10px_30px_hsl(var(--gold)/0.3)] hover:scale-[1.02] active:scale-[0.98] active:shadow-sm",
        hero: "bg-gradient-to-r from-deep-red to-deep-red-light text-cream font-semibold text-lg shadow-md hover:shadow-[0_10px_30px_hsl(var(--deep-red)/0.4)] hover:scale-[1.02] active:scale-[0.98] active:shadow-sm",
        "deep-red": "bg-gradient-to-r from-deep-red to-deep-red-light text-cream font-semibold shadow-md hover:shadow-[0_10px_30px_hsl(var(--deep-red)/0.4)] hover:scale-[1.02] active:scale-[0.98] active:shadow-sm",
      },
      size: {
        default: "h-11 px-5 py-2.5 min-w-[44px]",
        sm: "h-10 rounded-md px-4 min-w-[44px]",
        lg: "h-12 rounded-md px-8 min-w-[44px]",
        xl: "h-14 rounded-md px-10 text-lg min-w-[44px]",
        icon: "h-11 w-11 min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  hapticFeedback?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, hapticFeedback = true, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (hapticFeedback) {
          haptic('light');
        }
        onClick?.(e);
      },
      [hapticFeedback, onClick]
    );
    
    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        onClick={handleClick}
        {...props} 
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
